import { PropsWithChildren, ReactElement, useCallback, useContext, useState } from 'react';

import { PanelInstanceContext } from './PanelInstanceContext';
import { PanelInstance, PanelsContext } from './PanelsContext';
import { useRestoreStateFrom } from './usePanelStateHandler';

export interface PanelInstanceContextProviderProps {
  hidden?: boolean;
  bounds?: string | Element;
  panelInstance: PanelInstance;
}

/**
 * Provides access to closing/popping panels
 */
export const PanelInstanceContextProvider = ({
  panelInstance,
  bounds,
  hidden,
  children,
}: PropsWithChildren<PanelInstanceContextProviderProps>): ReactElement => {
  const { closePanel, bringPanelToFront, dockElements } = useContext(PanelsContext);
  const [title, setTitle] = useState('');
  const [dockId, setDockId] = useState<string>();

  const savedState = useRestoreStateFrom({ uniqueId: panelInstance.uniqueId });
  const [poppedOut, setPoppedOut] = useState(savedState?.panelPoppedOut ?? panelInstance.poppedOut);

  const togglePopOut = useCallback(() => {
    // oxlint-disable-next-line react/immutability
    panelInstance.window = null;
    setPoppedOut(!poppedOut);
  }, [panelInstance, poppedOut]);

  const dock = useCallback(
    (id: string) => {
      if (poppedOut) togglePopOut();
      setDockId(id);
    },
    [poppedOut, togglePopOut],
  );

  const undock = useCallback(() => setDockId(undefined), []);

  const uniqueId = panelInstance.uniqueId;

  return (
    <PanelInstanceContext.Provider
      value={{
        title,
        setTitle,
        uniqueId,
        bounds,
        panelClose: () => {
          panelInstance.window?.close();
          // oxlint-disable-next-line react/immutability
          panelInstance.window = null;
          closePanel(uniqueId);
        },
        panelTogglePopout: togglePopOut,
        panelPoppedOut: poppedOut,
        setPanelWindow: (w: Window) => {
          // oxlint-disable-next-line react/immutability
          panelInstance.window = w;
        },
        bringPanelToFront: () => bringPanelToFront(uniqueId),
        zIndex: panelInstance.zIndex,
        docked: !poppedOut && !!dockId && !!dockElements[dockId],
        dockId,
        dock,
        undock,
        hidden,
      }}
    >
      {children}
    </PanelInstanceContext.Provider>
  );
};
